import { PropertyChange } from './types';

// Helper functions to create human-readable descriptions for actions

export function createElementActionDescription(
    action: 'create' | 'delete' | 'move' | 'resize' | 'copy',
    elementType: UIElementType,
    elementId: string,
    additionalInfo?: string,
): string {
    const elementName = getElementTypeName(elementType);

    switch (action) {
        case 'create':
            return `Created ${elementName}`;
        case 'delete':
            return `Deleted ${elementName}`;
        case 'move':
            return `Moved ${elementName}${
                additionalInfo ? ` ${additionalInfo}` : ''
            }`;
        case 'resize':
            return `Resized ${elementName}`;
        case 'copy':
            return `Copied ${elementName}`;
        default:
            return `Modified ${elementName}`;
    }
}

export function createPropertyChangeDescription(
    targetType: 'element' | 'screen' | 'experience',
    targetName: string,
    changes: PropertyChange[],
): string {
    if (changes.length === 0) {
        return `Modified ${targetName}`;
    }

    if (changes.length === 1) {
        const change = changes[0];
        return `Changed ${change.displayName} of ${targetName}`;
    }

    if (changes.length <= 3) {
        const propertyNames = changes.map((c) => c.displayName).join(', ');
        return `Changed ${propertyNames} of ${targetName}`;
    }

    return `Changed ${changes.length} properties of ${targetName}`;
}

export function createScreenActionDescription(
    action: 'create' | 'delete',
    screenId: string,
    additionalInfo?: string,
): string {
    switch (action) {
        case 'create':
            return `Created screen${
                additionalInfo ? ` ${additionalInfo}` : ''
            }`;
        case 'delete':
            return `Deleted screen${
                additionalInfo ? ` ${additionalInfo}` : ''
            }`;
        default:
            return `Modified screen`;
    }
}

export function createCanvasActionDescription(
    action: 'zoom' | 'pan' | 'select',
    details?: string,
): string {
    switch (action) {
        case 'zoom':
            return `Zoomed canvas${details ? ` ${details}` : ''}`;
        case 'pan':
            return `Panned canvas${details ? ` ${details}` : ''}`;
        case 'select':
            return `Selected ${details || 'item'}`;
        default:
            return 'Canvas operation';
    }
}

// Helper to get human-readable element type names
function getElementTypeName(elementType: UIElementType): string {
    const typeNames: Partial<Record<UIElementType, string>> = {
        text: 'Text Element',
        button: 'Button',
        image: 'Image',
        input: 'Input Field',
        dynamicText: 'Dynamic Text',
        currentSessionQR: 'QR Code',
        paymentQR: 'Payment QR',
        outputPreview: 'Output Preview',
        galleryGrid: 'Gallery Grid',
        mediaGrid: 'Media Grid',
        sessionGrid: 'Session Grid',
        liveView: 'Live View',
        selectedQR: 'Selected QR',
        group: 'Group',
        artworkSelector: 'Artwork Selector',
        codeScanner: 'Code Scanner',
        phoneKeypad: 'Phone Keypad',
    };

    return typeNames[elementType] || 'Element';
}

// Helper to get human-readable property names
export function getPropertyDisplayName(propertyPath: string): string {
    const propertyNames: Record<string, string> = {
        // Text properties
        'metadata.text': 'Text Content',
        'metadata.fontSize': 'Font Size',
        'metadata.fontWeight': 'Font Weight',
        'metadata.textAlign': 'Text Alignment',
        'metadata.color': 'Text Color',

        // Layout properties
        horizontalLocation: 'Horizontal Position',
        verticalLocation: 'Vertical Position',
        'metadata.width': 'Width',
        'metadata.height': 'Height',
        'metadata.horizontalOffset': 'Horizontal Offset',
        'metadata.verticalOffset': 'Vertical Offset',

        // Display properties
        'displayProperties.backgroundColor': 'Background Color',
        'displayProperties.foregroundColor': 'Text Color',
        'displayProperties.buttonColor': 'Button Color',
        'displayProperties.buttonTextColor': 'Button Text Color',
        'displayProperties.backgroundMediaUrl': 'Background Image',
        'displayProperties.horizontalPadding': 'Horizontal Padding',
        'displayProperties.verticalPadding': 'Vertical Padding',

        // Button properties
        'metadata.buttonText': 'Button Text',
        'metadata.buttonAction': 'Button Action',

        // Image properties
        'metadata.imageUrl': 'Image URL',
        'metadata.altText': 'Alt Text',

        // Video properties
        'metadata.videoUrl': 'Video URL',
        'metadata.autoplay': 'Autoplay',
        'metadata.controls': 'Show Controls',

        // QR properties
        'metadata.qrData': 'QR Code Data',
        'metadata.qrSize': 'QR Code Size',

        // Input properties
        'metadata.placeholder': 'Placeholder Text',
        'metadata.inputType': 'Input Type',
        'metadata.required': 'Required Field',

        // Layer properties
        layer: 'Layer',
        order: 'Order',

        // Screen properties
        'stage.type': 'Screen Type',
        'stage.metadata': 'Screen Settings',
    };

    return (
        propertyNames[propertyPath] ||
        propertyPath.split('.').pop() ||
        'Property'
    );
}

// Helper to create property change objects
export function createPropertyChange(
    path: string,
    oldValue: any,
    newValue: any,
    displayName?: string,
): PropertyChange {
    return {
        path,
        oldValue,
        newValue,
        displayName: displayName || getPropertyDisplayName(path),
    };
}

// Helper to format values for display in history
export function formatValueForDisplay(value: any): string {
    if (value === null || value === undefined) {
        return 'None';
    }

    if (typeof value === 'string') {
        if (value.length > 30) {
            return `"${value.substring(0, 27)}..."`;
        }
        return `"${value}"`;
    }

    if (typeof value === 'number') {
        return value.toString();
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value).substring(0, 30) + '...';
    }

    return String(value);
}

// Helper to create detailed change summary for history UI
export function createChangesSummary(changes: PropertyChange[]): string {
    if (changes.length === 0) return 'No changes';

    if (changes.length === 1) {
        const change = changes[0];
        const oldVal = formatValueForDisplay(change.oldValue);
        const newVal = formatValueForDisplay(change.newValue);
        return `${change.displayName}: ${oldVal} → ${newVal}`;
    }

    return changes
        .map((change) => {
            const oldVal = formatValueForDisplay(change.oldValue);
            const newVal = formatValueForDisplay(change.newValue);
            return `${change.displayName}: ${oldVal} → ${newVal}`;
        })
        .join('\n');
}
